import React from 'react';
import { useSearchId } from 'clientState/providers/SearchResponseProvider';
import { usePerformedSearchRequest } from 'clientState/providers/SearchRequestProvider';
import { useItinerary } from 'clientState/providers/ItinerarySelectionProvider/ItineraryProvider';
import { differenceInCalendarDays, startOfToday } from 'date-fns';
import { useDisplayedItinerariesQuery } from 'clientState/providers/DisplayedItinerariesProvider';
import { usePrime } from 'clientState/providers/PrimeProvider';

export const usePriceFreezeSelection = () => {
  const searchId = useSearchId();
  const itinerary = useItinerary();
  const id = itinerary.fareItineraryKey || itinerary.id;
  const fareItineraryKey = id.split(',')[0]?.split('_')[0];
  const segmentKeys =
    itinerary.segmentKeys || itinerary.legs.map((leg) => leg.segmentKeys[0]);

  return {
    searchId: itinerary.searchId || searchId,
    fareItineraryKey,
    segmentKeys,
  };
};

export const useSearchCriteria = () => {
  const searchRequest = usePerformedSearchRequest() || {};
  const {
    tripType = '',
    itinerary: {
      segments = [],
      numAdults = 0,
      numChildren = 0,
      numInfants = 0,
    } = {},
  } = searchRequest;

  return {
    paxNumber: {
      numInfants,
      numChildren,
      numAdults,
    },
    tripType,
    departureDate: parseDate(segments[0]?.date),
  };
};

export function usePriceFreezeInScope() {
  const searchCriteria = useSearchCriteria();
  const { data: displayedItineraryData } = useDisplayedItinerariesQuery();
  const isMeta =
    displayedItineraryData && displayedItineraryData.externalSelection;
  const itinerary = useItinerary();

  return (
    isInScope(searchCriteria) &&
    !isMeta &&
    isOnlyPlane(itinerary.transportTypes)
  );
}

export const withPrimeCheck = (Component) => (props) => {
  const { isLoggedPrimeUser } = usePrime();

  if (!isLoggedPrimeUser) {
    return null;
  }

  return <Component {...props} />;
};

export const withUserLoggedCheck = (Component) => (props) => {
  const { isLoggedPrimeUser, isUserLogged } = usePrime();

  if (isUserLogged && !isLoggedPrimeUser) {
    return null;
  }

  return <Component {...props} />;
};

function isInScope(searchCriteria = {}) {
  return (
    daysInAdvance(searchCriteria.departureDate) >= 4 &&
    searchCriteria.tripType !== 'MULTIPLE_DESTINATIONS'
  );
}

function isOnlyPlane(transportTypes = []) {
  return transportTypes.every((transport) => transport === 'PLANE');
}

export function daysInAdvance(departureDate) {
  return differenceInCalendarDays(departureDate, startOfToday());
}

function parseDate(dateStr = '') {
  const [year, month, day] = dateStr.split('-');
  return Date.UTC(Number(year), Number(month) - 1, Number(day));
}
